class ImageExplorer {
  constructor(container) {
    this.container = container;
    this.canvas = container.querySelector('.image-explorer-canvas');
    this.image = container.querySelector('.explorer-image');
    this.resetBtn = container.querySelector('.explorer-reset-btn');
    
    // Settings
    this.autoAnimate = container.dataset.autoAnimate === 'true';
    this.animationSpeed = parseFloat(container.dataset.animationSpeed) || 20;
    this.resetTimeout = parseInt(container.dataset.resetTimeout) || 15;
    
    // Image dimensions
    this.imageWidth = 0;
    this.imageHeight = 0;
    this.imageScale = 1; // Scale to fit image to container (like object-fit: cover)
    
    // State
    this.scale = parseFloat(container.dataset.zoomLevel) / 100 || 2;
    this.minScale = 1;
    this.maxScale = 4;
    this.x = 0;
    this.y = 0;
    this.isDragging = false;
    this.isUserControlled = false;
    this.hasInteracted = false;
    
    // Animation state
    this.targetX = 0;
    this.targetY = 0;
    this.animationFrame = null;
    this.lastChangeTime = Date.now();
    this.changeInterval = 5000; // Change target every 5 seconds
    this.inactivityTimer = null;
    
    // Touch state
    this.touches = [];
    this.lastTouchDistance = 0;
    
    // Drag state
    this.startX = 0;
    this.startY = 0;
    this.lastX = 0;
    this.lastY = 0;
    
    this.init();
  }

  init() {
    // Wait for image to load
    if (this.image) {
      if (this.image.complete) {
        this.setup();
      } else {
        this.image.addEventListener('load', () => this.setup());
      }
    }
  }

  setup() {
    // Calculate image dimensions and scale to cover container
    this.calculateImageSize();
    
    // Apply initial sizing to image
    this.updateImageSize();
    this.updateTransform();
    
    // Mouse events
    this.container.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    document.addEventListener('mouseup', () => this.handleMouseUp());
    this.container.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
    
    // Touch events
    this.container.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
    this.container.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    this.container.addEventListener('touchend', (e) => this.handleTouchEnd(e));
    
    // Reset button
    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', () => this.reset());
    }
    
    // Handle window resize
    window.addEventListener('resize', () => this.handleResize());
    
    // Start auto-animation
    if (this.autoAnimate) {
      this.pickNewTarget();
      this.startAnimation();
    }
  }

  calculateImageSize() {
    const containerRect = this.container.getBoundingClientRect();
    this.imageWidth = this.image.naturalWidth;
    this.imageHeight = this.image.naturalHeight;
    
    // Calculate scale to cover container (like object-fit: cover)
    const containerRatio = containerRect.width / containerRect.height;
    const imageRatio = this.imageWidth / this.imageHeight;
    
    if (imageRatio > containerRatio) {
      // Image is wider - fit to height
      this.imageScale = containerRect.height / this.imageHeight;
    } else {
      // Image is taller - fit to width
      this.imageScale = containerRect.width / this.imageWidth;
    }
  }

  updateImageSize() {
    if (this.image) {
      this.image.style.width = `${this.imageWidth}px`;
      this.image.style.height = `${this.imageHeight}px`;
    }
  }

  handleResize() {
    this.calculateImageSize();
    this.updateImageSize();
    this.constrainPosition();
    this.updateTransform();
  }

  // Animation methods
  startAnimation() {
    if (this.animationFrame) return;
    
    this.canvas.classList.add('is-animating');
    const animate = () => {
      if (this.isUserControlled) {
        this.canvas.classList.remove('is-animating');
        return;
      }
      
      this.updateAutoPosition();
      this.updateTransform();
      this.animationFrame = requestAnimationFrame(animate);
    };
    
    this.animationFrame = requestAnimationFrame(animate);
  }

  stopAnimation() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.canvas.classList.remove('is-animating');
    }
  }

  updateAutoPosition() {
    const now = Date.now();
    
    // Pick new target periodically
    if (now - this.lastChangeTime > this.changeInterval) {
      this.pickNewTarget();
      this.lastChangeTime = now;
    }
    
    // Smooth easing towards target
    const ease = 0.02 * (this.animationSpeed / 20);
    this.x += (this.targetX - this.x) * ease;
    this.y += (this.targetY - this.y) * ease;
    
    // Constrain position
    this.constrainPosition();
  }

  pickNewTarget() {
    const bounds = this.getBounds();
    
    // Pick random position within valid bounds
    this.targetX = bounds.minX + Math.random() * (bounds.maxX - bounds.minX);
    this.targetY = bounds.minY + Math.random() * (bounds.maxY - bounds.minY);
  }

  // Interaction methods
  handleMouseDown(e) {
    if (e.button !== 0) return;
    
    this.startDrag(e.clientX, e.clientY);
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    
    this.drag(e.clientX, e.clientY);
    e.preventDefault();
  }

  handleMouseUp() {
    this.endDrag();
  }

  handleTouchStart(e) {
    this.touches = Array.from(e.touches);
    
    if (this.touches.length === 1) {
      this.startDrag(this.touches[0].clientX, this.touches[0].clientY);
    } else if (this.touches.length === 2) {
      this.lastTouchDistance = this.getTouchDistance();
      this.stopUserControl();
    }
    
    e.preventDefault();
  }

  handleTouchMove(e) {
    this.touches = Array.from(e.touches);
    
    if (this.touches.length === 1 && this.isDragging) {
      this.drag(this.touches[0].clientX, this.touches[0].clientY);
    } else if (this.touches.length === 2) {
      this.handlePinchZoom();
    }
    
    e.preventDefault();
  }

  handleTouchEnd(e) {
    this.touches = Array.from(e.touches);
    
    if (this.touches.length === 0) {
      this.endDrag();
    } else if (this.touches.length === 1) {
      this.lastTouchDistance = 0;
    }
  }

  handleWheel(e) {
    e.preventDefault();
    
    this.startUserControl();
    
    const delta = e.deltaY * -0.001;
    const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale + delta));
    
    // Zoom towards cursor position
    const rect = this.container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    this.zoomTo(newScale, mouseX, mouseY);
    this.startInactivityTimer();
  }

  handlePinchZoom() {
    const distance = this.getTouchDistance();
    
    if (this.lastTouchDistance > 0) {
      const delta = (distance - this.lastTouchDistance) * 0.01;
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale + delta));
      
      // Zoom towards center of pinch
      const rect = this.container.getBoundingClientRect();
      const centerX = (this.touches[0].clientX + this.touches[1].clientX) / 2 - rect.left;
      const centerY = (this.touches[0].clientY + this.touches[1].clientY) / 2 - rect.top;
      
      this.zoomTo(newScale, centerX, centerY);
    }
    
    this.lastTouchDistance = distance;
    this.startInactivityTimer();
  }

  getTouchDistance() {
    if (this.touches.length < 2) return 0;
    
    const dx = this.touches[0].clientX - this.touches[1].clientX;
    const dy = this.touches[0].clientY - this.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  startDrag(clientX, clientY) {
    this.isDragging = true;
    this.startX = clientX;
    this.startY = clientY;
    this.lastX = this.x;
    this.lastY = this.y;
    
    this.container.classList.add('is-dragging');
    this.startUserControl();
  }

  drag(clientX, clientY) {
    if (!this.isDragging) return;
    
    const dx = clientX - this.startX;
    const dy = clientY - this.startY;
    
    this.x = this.lastX + dx;
    this.y = this.lastY + dy;
    
    this.constrainPosition();
    this.updateTransform();
    this.startInactivityTimer();
  }

  endDrag() {
    this.isDragging = false;
    this.container.classList.remove('is-dragging');
  }

  zoomTo(newScale, originX, originY) {
    const rect = this.container.getBoundingClientRect();
    
    // Convert origin to be relative to container center
    const centerOriginX = originX - rect.width / 2;
    const centerOriginY = originY - rect.height / 2;
    
    // Calculate position in image coordinates before zoom
    const oldTotalScale = this.imageScale * this.scale;
    const imageX = (centerOriginX - this.x) / oldTotalScale;
    const imageY = (centerOriginY - this.y) / oldTotalScale;
    
    // Update scale
    this.scale = newScale;
    
    // Recalculate position to keep the same point under the cursor
    const newTotalScale = this.imageScale * this.scale;
    this.x = centerOriginX - imageX * newTotalScale;
    this.y = centerOriginY - imageY * newTotalScale;
    
    this.constrainPosition();
    this.updateTransform();
  }

  // Helper methods
  startUserControl() {
    if (!this.isUserControlled) {
      this.isUserControlled = true;
      this.hasInteracted = true;
      this.stopAnimation();
      this.container.classList.add('is-interacting', 'user-has-interacted');
    }
  }

  stopUserControl() {
    this.isUserControlled = false;
    this.container.classList.remove('is-interacting');
  }

  startInactivityTimer() {
    clearTimeout(this.inactivityTimer);
    
    if (this.resetTimeout > 0 && this.autoAnimate) {
      this.inactivityTimer = setTimeout(() => {
        this.reset();
      }, this.resetTimeout * 1000);
    }
  }

  getBounds() {
    const containerRect = this.container.getBoundingClientRect();
    
    // Total scale applied to the image (base scale to cover + user scale)
    const totalScale = this.imageScale * this.scale;
    
    // Actual scaled image dimensions
    const scaledWidth = this.imageWidth * totalScale;
    const scaledHeight = this.imageHeight * totalScale;
    
    // Calculate how much the image exceeds the container
    const excessWidth = scaledWidth - containerRect.width;
    const excessHeight = scaledHeight - containerRect.height;
    
    // Bounds allow exploring the full image
    // Center is 0,0 and we can pan in either direction to see all parts
    return {
      minX: -excessWidth / 2,
      maxX: excessWidth / 2,
      minY: -excessHeight / 2,
      maxY: excessHeight / 2
    };
  }

  constrainPosition() {
    const bounds = this.getBounds();
    
    this.x = Math.max(bounds.minX, Math.min(bounds.maxX, this.x));
    this.y = Math.max(bounds.minY, Math.min(bounds.maxY, this.y));
  }

  updateTransform() {
    // Apply both the base scale (to cover) and user scale, plus position
    const totalScale = this.imageScale * this.scale;
    this.canvas.style.transform = `translate(${this.x}px, ${this.y}px) scale(${totalScale})`;
  }

  reset() {
    this.stopUserControl();
    this.scale = parseFloat(this.container.dataset.zoomLevel) / 100 || 2;
    this.x = 0;
    this.y = 0;
    
    this.updateTransform();
    
    if (this.autoAnimate) {
      this.lastChangeTime = Date.now();
      this.pickNewTarget();
      this.startAnimation();
    }
    
    clearTimeout(this.inactivityTimer);
  }
}

// Initialize all image explorers on the page
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.image-explorer-container').forEach(container => {
    new ImageExplorer(container);
  });
});

// Handle section reloads in theme editor
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.image-explorer-container');
    if (container) {
      new ImageExplorer(container);
    }
  });
}